// Grouped model of the organizer sidebar: four business modules (Tickets, Reservas de mesa, Canchas, Beauty)
// plus a "General" group for screens that serve any business.
//
// The screens themselves live in ONE flat config owned by Layout.jsx (route, label, icon...). A link
// opts into a module with `module: "<key>"`; a link without `module` is General. Adding a screen to a
// module is therefore a one-line change in that config, e.g.
//   { to: "/organizer/estadisticas/canchas", label: "Estadísticas", module: "canchas" }
//
// This file only shapes and queries that config. It is pure on purpose (no React, no router, no
// permissions): who may see a screen is decided by the caller and handed in as a predicate, so the
// existing rules (enabled links, puedeVerRuta, negocioMuestraRuta) keep a single owner.

/** Modules in the order the sidebar shows them. */
export const NAV_MODULES = [
  { key: "eventos", label: "Tickets" },
  { key: "mesas", label: "Reservas de mesa" },
  { key: "canchas", label: "Canchas" },
  { key: "beauty", label: "Beauty" },
];

/**
 * Screens that belong to a module but have no sidebar link of their own (reached from another
 * screen: an event's orders, its tickets, the scanners list). Visiting them opens their module, so
 * the user keeps their place in the menu. They only count when the module itself is visible.
 */
export const MODULE_EXTRA_ROUTES = {
  eventos: ["/organizer/orders", "/organizer/tickets", "/organizer/scanners"],
};

/**
 * Splits a flat link config into modules and General, keeping only the links `isVisible` accepts.
 *
 * - Modules come out in NAV_MODULES order, whatever the order of the config.
 * - A module left with no visible screen is dropped: the business or employee never sees an empty
 *   module button.
 * - A link pointing at an unknown module is dropped rather than leaked into General, so a typo in
 *   the config cannot expose a screen in the wrong place.
 *
 * @param {Array<{to: string, label: string, module?: string}>} links
 * @param {(link: object) => boolean} [isVisible]
 * @returns {{ modules: Array<{key: string, label: string, links: object[]}>, general: object[] }}
 */
export const buildNavGroups = (links, isVisible = () => true) => {
  const visible = links.filter(isVisible);

  const modules = NAV_MODULES.map((definition) => ({
    ...definition,
    links: visible.filter((link) => link.module === definition.key),
  })).filter((group) => group.links.length > 0);

  const general = visible.filter((link) => !link.module);

  return { modules, general };
};

// "/organizer/canchas/" and "/organizer/canchas" are the same screen; the root stays "/".
const normalizePath = (pathname) => {
  const path = String(pathname || "");
  return path.length > 1 ? path.replace(/\/+$/, "") : path;
};

// A link matches its own path and anything nested under it (".../events/12/edit"), compared by
// whole segments so "/organizer/reservasXYZ" does not match "/organizer/reservas". A link flagged
// `end` (the panel home, "/organizer") only matches itself: as a prefix it would swallow every
// other panel route, mirroring the `end` prop of the sidebar NavLink.
const matchLength = (link, path) => {
  const target = normalizePath(link.to);
  if (path === target) return target.length;
  if (link.end) return -1;
  return path.startsWith(`${target}/`) ? target.length : -1;
};

/**
 * Key of the module that holds `pathname`, or null when no module screen matches (General screens,
 * unknown routes). A module's MODULE_EXTRA_ROUTES count as its screens here. When several screens match, the longest route wins, so a nested screen can live
 * in a different module than its parent route.
 *
 * @param {Array<{key: string, links: object[]}>} modules - the `modules` of buildNavGroups
 * @param {string} pathname
 * @returns {string | null}
 */
export const findModuleKeyForPath = (modules, pathname) => {
  const path = normalizePath(pathname);
  if (!path) return null;

  let bestKey = null;
  let bestLength = -1;

  modules.forEach((group) => {
    const extraLinks = (MODULE_EXTRA_ROUTES[group.key] || []).map((to) => ({ to }));
    [...group.links, ...extraLinks].forEach((link) => {
      const length = matchLength(link, path);
      if (length > bestLength) {
        bestLength = length;
        bestKey = group.key;
      }
    });
  });

  return bestKey;
};
